import { useState } from 'react'
import type { DateFilter } from '@/utils/dateFilters'

interface DateRangeFilterProps {
  currentFilter: DateFilter
  onFilterChange: (filter: DateFilter) => void
  className?: string
}

export function DateRangeFilter({
  currentFilter,
  onFilterChange,
  className = ''
}: DateRangeFilterProps) {
  const [showCustomRange, setShowCustomRange] = useState(false)
  const [customFromDate, setCustomFromDate] = useState('')
  const [customToDate, setCustomToDate] = useState('')

  const filters: Array<{ value: DateFilter; label: string }> = [
    { value: 'all', label: 'הכל' },
    { value: 'week', label: 'השבוע הקרוב' },
    { value: 'month', label: 'החודש הנוכחי' },
    { value: 'future', label: 'עתיד בלבד' }
  ]

  const handleCustomRangeApply = () => {
    if (customFromDate && customToDate) {
      localStorage.setItem('customDateFrom', customFromDate)
      localStorage.setItem('customDateTo', customToDate)
      onFilterChange('custom' as DateFilter)
      setShowCustomRange(false)
    }
  }

  const handleFilterClick = (value: DateFilter) => {
    if (value === 'custom' as DateFilter) {
      setShowCustomRange(true)
    } else {
      onFilterChange(value)
      setShowCustomRange(false)
    }
  }

  return (
    <div className={`bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-200 ${className}`}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-bold text-gray-900 font-hebrew">
            סינון לפי תאריך
          </h3>
        </div>

        <div className="flex items-center gap-3 justify-start flex-wrap">
          {filters.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleFilterClick(value)}
              className={`px-4 py-2 rounded-full text-sm font-bold font-hebrew transition-all duration-200 min-h-[44px] touch-manipulation ${
                currentFilter === value
                  ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-md hover:bg-blue-700 transform scale-105'
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              {label}
            </button>
          ))}

          <button
            onClick={() => setShowCustomRange(!showCustomRange)}
            className={`px-4 py-2 rounded-full text-sm font-bold font-hebrew transition-all duration-200 min-h-[44px] touch-manipulation ${
              currentFilter === 'custom'
                ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-md hover:bg-blue-700 transform scale-105'
                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
          >
            טווח תאריכים
          </button>
        </div>

        {showCustomRange && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end" dir="rtl">
              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-gray-700 font-hebrew mb-2">
                  מתאריך:
                </label>
                <input
                  type="date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-hebrew text-right"
                  dir="rtl"
                />
              </div>

              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-gray-700 font-hebrew mb-2">
                  עד תאריך:
                </label>
                <input
                  type="date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  min={customFromDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-hebrew text-right"
                  dir="rtl"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCustomRangeApply}
                  disabled={!customFromDate || !customToDate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-hebrew disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  החל
                </button>
                <button
                  onClick={() => setShowCustomRange(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-hebrew min-h-[44px]"
                >
                  ביטול
                </button>
              </div>
            </div>

            {customFromDate && customToDate && (
              <p className="text-sm text-blue-700 font-hebrew">
                מציג תאריכים מ-{new Date(customFromDate).toLocaleDateString('he-IL')} עד {new Date(customToDate).toLocaleDateString('he-IL')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
